/**
 * Sistema de cache mejorado para el Dashboard
 * - Almacena datos reales, no solo flags
 * - Persiste en sessionStorage entre remounts
 * - Expira automáticamente después de un tiempo configurable
 * - Incluye timestamp para invalidación inteligente
 */

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos por defecto
const CACHE_PREFIX = 'dashboard_cache_';

// Helper para obtener la clave del cache
function getCacheKey(type, ...args) {
  return `${CACHE_PREFIX}${type}_${args.join('_')}`;
}

// Helper para verificar si el cache es válido
function isCacheValid(cacheData) {
  if (!cacheData || !cacheData.timestamp) return false;
  const now = Date.now();
  const age = now - cacheData.timestamp;
  return age < CACHE_DURATION;
}

// Helper para obtener datos del cache
function getFromCache(key) {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;
    
    const data = JSON.parse(cached);
    if (isCacheValid(data)) {
      console.log(`✅ Cache HIT: ${key} (age: ${Math.round((Date.now() - data.timestamp) / 1000)}s)`);
      return data.value;
    } else {
      console.log(`⏰ Cache EXPIRED: ${key}`);
      sessionStorage.removeItem(key);
      return null;
    }
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
}

// Helper para guardar datos en el cache
function saveToCache(key, value) {
  try {
    const cacheData = {
      value,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(key, JSON.stringify(cacheData));
    console.log(`💾 Cache SAVED: ${key}`);
  } catch (error) {
    console.error('Error saving to cache:', error);
  }
}

// ============= CHART CACHE =============

export function getCachedChartData(userId, compacctnum, weekStart) {
  const key = getCacheKey('chart', userId, compacctnum, weekStart);
  return getFromCache(key);
}

export function saveCachedChartData(userId, compacctnum, weekStart, data) {
  const key = getCacheKey('chart', userId, compacctnum, weekStart);
  saveToCache(key, data);
}

export function shouldFetchChart(userId, compacctnum, weekStart) {
  const cached = getCachedChartData(userId, compacctnum, weekStart);
  return cached === null;
}

export function markChartFetched(userId, compacctnum, weekStart) {
  // Esta función ahora se mantiene por compatibilidad pero el cache real se hace en saveCachedChartData
  console.log(`📊 Chart marked as fetched for ${userId}-${compacctnum}-${weekStart}`);
}

// ============= JOB TABLE CACHE =============

export function getCachedJobTableData(compacct, jobType) {
  const key = getCacheKey('jobtable', compacct, jobType);
  return getFromCache(key);
}

export function saveCachedJobTableData(compacct, jobType, data) {
  const key = getCacheKey('jobtable', compacct, jobType);
  saveToCache(key, data);
}

export function shouldFetchJobTable(compacct, jobType) {
  const cached = getCachedJobTableData(compacct, jobType);
  return cached === null;
}

export function markJobTableFetched(compacct, jobType) {
  // Esta función se mantiene por compatibilidad
  console.log(`📋 Job table marked as fetched for ${compacct}-${jobType}`);
}

// ============= NAVIGATION CACHE =============

export function getCachedNavigationData(compacct) {
  const key = getCacheKey('navigation', compacct);
  return getFromCache(key);
}

export function saveCachedNavigationData(compacct, data) {
  const key = getCacheKey('navigation', compacct);
  saveToCache(key, data);
}

// ============= SUBSCRIPTION CACHE =============

export function getCachedSubscriptionCounter(compacct) {
  const key = getCacheKey('subscription_counter', compacct);
  return getFromCache(key);
}

export function saveCachedSubscriptionCounter(compacct, data) {
  const key = getCacheKey('subscription_counter', compacct);
  saveToCache(key, data);
}

export function getCachedSubscriptionCompany(compacct) {
  const key = getCacheKey('subscription_company', compacct);
  return getFromCache(key);
}

export function saveCachedSubscriptionCompany(compacct, data) {
  const key = getCacheKey('subscription_company', compacct);
  saveToCache(key, data);
}

// ============= CLEAR CACHE =============

export function clearDashboardCache() {
  console.log('🗑️ Clearing all dashboard cache');
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
}

// Invalidar cache específico
export function invalidateCache(type, ...args) {
  const key = getCacheKey(type, ...args);
  sessionStorage.removeItem(key);
  console.log(`🔄 Cache INVALIDATED: ${key}`);
}

